import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { getCurrentCompanyId } from '../lib/company';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { ClipboardCheck, FileImage, MessageCircle } from 'lucide-react';
import { money, fmtDate, waLink } from '../lib/share';
import DocShare from '../components/DocShare';
import { Input } from '../components/ui/input';

const ReservationRow = ({ r, onDone }) => {
  const [open, setOpen] = useState(false);
  const cp = r.credit_plan || {};
  const [f, setF] = useState({ document_type: 'nota_entrega', down_payment: cp.down_payment || 0, frequency: cp.frequency || '14', installments: cp.installments || 4, imei: r.imei || '' });
  const [done, setDone] = useState(null);
  const run = async (fn, msg) => { try { const res = await fn(); toast.success(msg); onDone(); return res; } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } };
  const close = async () => {
    const res = await run(() => api.closeReservation(r.reservation_id, { document_type: f.document_type, imei: f.imei || null, down_payment: parseFloat(f.down_payment) || 0,
      financing: r.mode === 'credito' ? { frequency: f.frequency, installments: parseInt(f.installments) || 1 } : null }), 'SalesPRO: Venta cerrada y recibo generado');
    if (res) setDone(res.data);
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`reservation-${r.reservation_id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <p className="font-bold text-[#022C22]">{r.client_name} · {r.product_name} · {money(r.price)}</p>
          <p className="text-xs text-[#64748B]">{r.mode === 'credito' ? 'Crédito' : 'Contado'} · {r.delivery === 'delivery' ? `Delivery: ${r.address}` : 'Retiro en tienda'} {r.scheduled_date ? `· ${fmtDate(r.scheduled_date)}` : ''} · IMEI {r.imei || '-'} · {fmtDate(r.created_at)}</p>
        </div>
        <span className="text-xs px-2 py-0.5 h-fit rounded-full font-semibold bg-slate-100">{r.status}</span>
      </div>
      {r.status === 'pendiente' && !done && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setOpen(!open)} data-testid={`res-close-toggle-${r.reservation_id}`}>Cerrar venta</Button>
          <Button size="sm" variant="outline" onClick={() => run(() => api.releaseReservation(r.reservation_id), 'SalesPRO: Reserva liberada; el equipo volvió al inventario')} data-testid={`res-release-${r.reservation_id}`}>Liberar</Button>
        </div>
      )}
      {open && !done && (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
          <select value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })} className="px-2 py-2 border rounded-md text-sm"><option value="nota_entrega">Nota de entrega</option><option value="factura">Factura</option></select>
          <Input placeholder="IMEI" value={f.imei} onChange={(e) => setF({ ...f, imei: e.target.value })} />
          {r.mode === 'credito' && <>
            <Input type="number" placeholder="Inicial" value={f.down_payment} onChange={(e) => setF({ ...f, down_payment: e.target.value })} data-testid={`res-down-${r.reservation_id}`} />
            <select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} className="px-2 py-2 border rounded-md text-sm"><option value="7">7 días</option><option value="14">14 días</option><option value="mensual">Mensual</option></select>
            <Input type="number" placeholder="Cuotas" value={f.installments} onChange={(e) => setF({ ...f, installments: e.target.value })} />
          </>}
          <Button size="sm" onClick={close} data-testid={`res-close-submit-${r.reservation_id}`}>Confirmar cierre</Button>
        </div>
      )}
      {(done || r.sale_id) && <DocShare kind="sale" refId={(done || r).sale_id} label="Recibo" />}
    </div>
  );
};

const TAG = { 'Pendiente': 'bg-slate-100 text-slate-700', 'En Verificación': 'bg-blue-100 text-blue-700', 'Aprobado': 'bg-green-100 text-green-700', 'Rechazado': 'bg-red-100 text-red-700' };

export default function CreditQueue() {
  const [apps, setApps] = useState([]);
  const [recs, setRecs] = useState([]);
  const [res, setRes] = useState([]);
  const cid = getCurrentCompanyId() || undefined;
  const load = async () => {
    try {
      const [a, r] = await Promise.all([api.getCreditApplications({ company_id: cid }), api.getReceipts({ company_id: cid })]);
      setApps(a.data); setRecs(r.data);
      api.getReservations({ company_id: cid }).then((x) => setRes(x.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error al cargar'); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (fn, id, status) => {
    try { await fn(id, { status }); toast.success(`SalesPRO: Marcado como ${status}`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); }
  };
  const Actions = ({ fn, id, opts }) => (
    <div className="flex flex-wrap gap-1">{opts.map((s) => <Button key={s} size="sm" variant="outline" className="h-7 text-xs" data-testid={`decide-${id}-${s.replace(/ /g, '-')}`} onClick={() => decide(fn, id, s)}>{s}</Button>)}</div>
  );
  return (
    <div className="space-y-4" data-testid="credit-queue-page">
      <h1 className="font-heading text-3xl font-black text-[#022C22] flex items-center gap-2"><ClipboardCheck /> Solicitudes y Comprobantes</h1>
      <Tabs defaultValue="reservations">
        <TabsList>
          <TabsTrigger value="reservations" data-testid="tab-reservations">Reservas ({res.filter((x) => x.status === 'pendiente').length})</TabsTrigger>
          <TabsTrigger value="apps" data-testid="tab-applications">Solicitudes de crédito ({apps.length})</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">Comprobantes de pago ({recs.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="reservations" className="space-y-2">
          {res.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay reservas.</p>}
          {res.map((r) => <ReservationRow key={r.reservation_id} r={r} onDone={load} />)}
        </TabsContent>
        <TabsContent value="apps" className="space-y-2">
          {apps.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay solicitudes.</p>}
          {apps.map((a) => (
            <div key={a.application_id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex flex-wrap justify-between gap-2" data-testid={`application-${a.application_id}`}>
              <div>
                <p className="font-bold text-[#022C22]">{a.name} <span className="text-xs text-[#94A3B8]">C.I. {a.cedula}</span></p>
                <p className="text-xs text-[#64748B]">{a.branch_name} · {fmtDate(a.created_at)} · {a.work_status || '-'} · ingresos {a.income_range || '-'} · equipo {money(a.product_price)}</p>
                <p className="text-xs">{a.max_credit != null && <>Pre-calificación: <b className={a.prequalified ? 'text-green-700' : 'text-red-600'}>{a.prequalified ? 'Apto' : 'No apto'}</b> hasta {money(a.max_credit)}</>}</p>
                <a href={waLink(a.phone, `Hola ${a.name}, te escribimos de Celular Beach por tu solicitud de crédito.`)} target="_blank" rel="noreferrer" className="text-xs text-green-700 inline-flex items-center gap-1 mt-1"><MessageCircle size={12} /> {a.phone}</a>
              </div>
              <div className="text-right space-y-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TAG[a.status]}`}>{a.status}</span>
                <Actions fn={api.decideCreditApplication} id={a.application_id} opts={['En Verificación', 'Aprobado', 'Rechazado']} />
              </div>
            </div>))}
        </TabsContent>
        <TabsContent value="receipts" className="space-y-2">
          {recs.length === 0 && <p className="text-sm text-[#94A3B8] py-8 text-center">No hay comprobantes.</p>}
          {recs.map((r) => (
            <div key={r.receipt_id} className="bg-white border border-[#E2E8F0] rounded-lg p-3 flex flex-wrap justify-between gap-2" data-testid={`receipt-${r.receipt_id}`}>
              <div>
                <p className="font-bold text-[#022C22]">{r.client_name} · {money(r.amount)}</p>
                <p className="text-xs text-[#64748B]">{r.method} · ref. {r.reference || '-'} · {r.source} · {fmtDate(r.created_at)} {r.note ? `· ${r.note}` : ''}</p>
                {r.file_id && <a href={api.fileUrl(r.file_id)} target="_blank" rel="noreferrer" className="text-xs text-primary inline-flex items-center gap-1 mt-1"><FileImage size={12} /> Ver comprobante</a>}
              </div>
              <div className="text-right space-y-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${TAG[r.status]}`}>{r.status}</span>
                {r.status !== 'Aprobado' && <Actions fn={api.decideReceipt} id={r.receipt_id} opts={['Aprobado', 'Rechazado']} />}
              </div>
            </div>))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
